/* global process */
// ── Global Conversation V2's TOPICS (`v2/chat/*`, busy-main-bff ChatTopicsV2Controller) ─────────
//
// The rail's list, the linked topics of an Ongoing, and the one save "offer" the box's card is
// showing. Registered by the modules folder like every other file here, and inert with the flag
// off: nothing dispatches into it (lib/chatTopicsV2Plugin.js is not installed and no V2 component
// is mounted), and every action that reaches the wire checks the flag again.
//
//   • `topics`      the rail's rows as the server sent them, normalised (lib/chatTopicsV2's `topicOf`);
//   • `byOngoing`   the topics linked to an Ongoing, kept by Ongoing id;
//   • `turnsById`   a topic's turns once read, for the Briefing and the split list;
//   • `offer`       what the save card is asking about: `{ mode, status, topicId, sessionId, title,
//                   matches, ... }`, modes being 'conversation', 'selection' and 'block'.
//
// A suggestion never writes anything. The only writes are the user's: Save here, a merge, a split,
// removing a link. The original conversation is never moved: V1's `assign` is not called from here.
//
// Reads are silent, as every chat read is: a rail that could not be fetched falls back to V1's.

import { nextTick } from 'vue'
import axios from 'axios'
import api from '@/lib/services/general.service'
import { messageIdForQuestion, savedRefsFrom } from '@/lib/chatSession'
import { answerMessageId, isServerId } from '@/lib/chatTaskBlocks'
import { ongoingRoute, savedConversationText } from '@/lib/saveConversation'
import { OPEN_MESSAGES } from '@/lib/store/modules/chatSessions'
import { isHomePath } from '@/lib/searchMode'
import { toast } from '@/lib/smartToast'
import router from '@/router'
import {
  AROUND_TAKE, GC_V2_TEXT, GLOBAL_CONVERSATION_V2, TOPICS_PAGE, aroundTopic, matchesFrom, mergeNewestPage,
  messagesFrom, savedSelectionText, scrollToTurn, selectionQuestion, topicIdFromPlace, topicIdOf, topicKey,
  topicTurns, topicsFrom, turnIndexOf, unwrap,
} from '@/lib/chatTopicsV2'

/** The save card does not wait on a suggestion longer than this; the old card takes over. */
export const SUGGEST_TIMEOUT_MS = 8000

const V2 = 'v2'
const post = (service, body = {}, config = {}) => api.insertWitDifferentVersion(service, body, V2, config)
// general.service has no v2 delete, and the additive rule keeps that file as it is.
const remove = (service) => axios.delete(`${process.env.VUE_APP_API_ADDRESS}/${V2}/${service}`)

const key = (id) => String(id || '')
const usable = (rootGetters) => GLOBAL_CONVERSATION_V2
  && !!rootGetters?.['user/isAuthenticated']
  && !rootGetters?.['incognito/on']

// Answers to a card that has since closed or moved on are dropped, by this ticket.
let offerTicket = 0

export const state = {
  topics: [],
  status: 'idle', // 'idle' | 'loading' | 'ready' | 'error'
  hasMore: false,
  loadingMore: false,
  // { [ongoingId]: { status, topics } }
  byOngoing: {},
  turnsById: {},
  offer: null,
  offerBusy: false,
  // The user turned the card down for this selection: the Research Pack card has it now.
  selectionDismissed: false,
  // The card's own save is in flight, so the save hook must not link the same thing again.
  ownSave: false,
}

export const getters = {
  topics: (s) => s.topics,
  status: (s) => s.status,
  hasMore: (s) => s.hasMore,
  loadingMore: (s) => s.loadingMore,
  /** The read failed and there is nothing to draw: the rail hands back to V1's. */
  failed: (s) => s.status === 'error' && !s.topics.length,
  ongoingTopics: (s) => (id) => s.byOngoing[key(id)]?.topics || [],
  ongoingStatus: (s) => (id) => s.byOngoing[key(id)]?.status || 'idle',
  /** The topics linked to an Ongoing: part of the Briefing's "N Conversations". */
  linkedCount: (s) => (id) => (s.byOngoing[key(id)]?.topics || []).length,
  turnsOf: (s) => (id) => s.turnsById[key(id)] || null,
  offer: (s) => s.offer,
  offerBusy: (s) => s.offerBusy,
  selectionDismissed: (s) => s.selectionDismissed,
  /** Is the card speaking for a selection? The Research Pack card steps aside while it is. */
  suggesting: (s) => s.offer?.mode === 'selection' && s.offer.status !== 'none',
  /** A pack's name, from the stories this tab has read. Empty when none of them has it. */
  packTitleOf: (s, g, rootState) => (id) => {
    const ongoing = rootState?.ongoing || {}
    const stories = [ongoing.currentStory, ...Object.values(ongoing.storyCache || {})]
    for (const story of stories) {
      const pack = (story?.packs || []).find((p) => String(p?.id) === String(id))
      if (pack?.title) return pack.title
    }
    return ''
  },
}

export const actions = {
  /** The newest page. It replaces what it covers, so a merged or expired topic leaves the rail. */
  async load({ commit, state: s, rootGetters }, { force = false } = {}) {
    if (!usable(rootGetters) || (s.status === 'loading' && !force)) return
    commit('SET_STATUS', 'loading')
    try {
      const page = topicsFrom(unwrap(await api.getV2('chat/topics', { take: TOPICS_PAGE })))
      commit('MERGE_PAGE', page)
      if (!s.hasMore) commit('SET_HAS_MORE', page.length >= TOPICS_PAGE)
      commit('SET_STATUS', 'ready')
    } catch {
      commit('SET_STATUS', 'error')
    }
  },

  /** One older page (the rail scrolled to its top). `before` is the oldest topic's start. */
  async loadMore({ commit, state: s, rootGetters }) {
    const oldest = s.topics[0]?.startedUtc
    if (!usable(rootGetters) || !s.hasMore || s.loadingMore || !oldest) return
    commit('SET_LOADING_MORE', true)
    try {
      const page = topicsFrom(unwrap(await api.getV2('chat/topics', { take: TOPICS_PAGE, before: encodeURIComponent(oldest) })))
      commit('PREPEND', page)
      commit('SET_HAS_MORE', page.length >= TOPICS_PAGE)
    } catch {
      commit('SET_HAS_MORE', false)
    } finally {
      commit('SET_LOADING_MORE', false)
    }
  },

  /** Place the session's unplaced turns now. `id` is a session id or the live row's placeholder. */
  async placeSession({ dispatch }, id) {
    const sessionId = await dispatch('chatSessions/resolveSessionId', id, { root: true })
    if (!sessionId) return { sessionId: null, topicId: null }
    const data = unwrap(await post(`chat/sessions/${sessionId}/place`))
    return { sessionId, topicId: topicIdFromPlace(data) }
  },

  /** An answer has been journalled: place it and refresh the rail. Never reaches the user. */
  async afterAnswer({ dispatch, rootGetters }) {
    if (!usable(rootGetters)) return
    try { await dispatch('placeSession', 'live') } catch { /* the worker places it instead */ }
    await dispatch('load', { force: true })
  },

  /** The topic a turn belongs to, by its message, or found by its question within the session. */
  async topicOfTurn(_, { sessionId, messageId, question }) {
    let id = isServerId(messageId) ? messageId : null
    if (!id && question && sessionId) {
      const data = unwrap(await api.get(`chat/sessions/${sessionId}/messages`, { take: OPEN_MESSAGES }))
      id = messageIdForQuestion(messagesFrom(data), question)
    }
    if (!id) return null
    return aroundTopic(unwrap(await api.getV2(`chat/topics/around/${id}`, { take: 1 })))?.id || null
  },

  /**
   * Which topic an action is about, its session placed first.
   *
   * A `topic:<id>` key names it. Otherwise it is the session's: the turn the action came from when
   * that is known, because the last topic is the wrong one once the user has moved on (a Task from
   * the egg answer must not link the Bali question after it), else the session's current topic.
   *
   * @returns {Promise<{topicId:string, sessionId:string|null}|null>}
   */
  async topicFor({ dispatch, state: s }, { id = 'live', messageId = null, question = '' } = {}) {
    const named = topicIdOf(id)
    if (named) {
      const sessionId = s.topics.find((t) => t.id === named)?.sessionId || null
      if (sessionId) await dispatch('placeSession', sessionId)
      return { topicId: named, sessionId }
    }
    const placed = await dispatch('placeSession', id)
    if (!placed.sessionId) return null
    // A turn the server cannot place yet is not an error: the session's current topic answers.
    const exact = await dispatch('topicOfTurn', { sessionId: placed.sessionId, messageId, question }).catch(() => null)
    if (exact) return { topicId: exact, sessionId: placed.sessionId }
    if (placed.topicId) return placed
    await dispatch('load', { force: true })
    const newest = [...s.topics].reverse().find((t) => t.sessionId === placed.sessionId)
    return newest ? { topicId: newest.id, sessionId: placed.sessionId } : null
  },

  /**
   * A rail row: the live one goes back to the live turns, any other shows that topic in the middle,
   * from its first turn. The whole topic is read rather than `topics/around`'s page, which would
   * stop a long topic at AROUND_TAKE messages; the rest is the deep link's own path.
   */
  async openRow({ commit, dispatch, rootGetters }, row) {
    if (row.live) return dispatch('chatSessions/backToLive', null, { root: true })
    try {
      const turns = topicTurns(messagesFrom(unwrap(await api.getV2(`chat/topics/${row.id}/messages`, {}))))
      commit('chatSessions/CACHE_TURNS', { id: topicKey(row.id), turns }, { root: true })
      if (rootGetters?.['research/active']) await dispatch('research/closeChat', null, { root: true })
      await dispatch('chatSessions/open', topicKey(row.id), { root: true })
      // After the render: with another thread on screen its first turn would be the one found.
      await nextTick()
      scrollToTurn(0)
    } catch {
      // The screen stays as it was, as a past session's failed open leaves it.
    }
  },

  /**
   * The deep link: open the exact turn (`topics/around/{messageId}`), not the latest message. The
   * topic may be older than the rail's loaded pages, so it joins them to be drawn as the open one.
   */
  async openAround({ commit, dispatch, rootGetters }, messageId) {
    if (!usable(rootGetters) || !messageId) return false
    try {
      const data = unwrap(await api.getV2(`chat/topics/around/${messageId}`, { take: AROUND_TAKE }))
      const topic = aroundTopic(data)
      if (!topic) return false
      const turns = topicTurns(messagesFrom(data))
      commit('ADD_TOPIC', topic)
      commit('chatSessions/CACHE_TURNS', { id: topicKey(topic.id), turns }, { root: true })
      // The chat is what the link opens, whatever research was left on Home.
      if (rootGetters?.['research/active']) await dispatch('research/closeChat', null, { root: true })
      await dispatch('chatSessions/open', topicKey(topic.id), { root: true })
      scrollToTurn(Math.max(0, turnIndexOf(turns, messageId)))
      return true
    } catch {
      return false
    }
  },

  /** A topic's turns, once: the Briefing reads them and the rail's split list names them. */
  async ensureTurns({ commit, state: s, rootGetters }, { topicId, force = false } = {}) {
    if (!GLOBAL_CONVERSATION_V2 || !rootGetters?.['user/isAuthenticated'] || !topicId) return
    if (!force && s.turnsById[key(topicId)]) return
    try {
      const turns = topicTurns(messagesFrom(unwrap(await api.getV2(`chat/topics/${topicId}/messages`, {}))))
      commit('SET_TURNS', { id: key(topicId), turns })
    } catch {
      // The row stays closed, and its title is still there.
    }
  },

  async ensureOngoing({ commit, state: s, rootGetters }, { ongoingId, force = false } = {}) {
    const id = key(ongoingId)
    if (!GLOBAL_CONVERSATION_V2 || !id || !rootGetters?.['user/isAuthenticated']) return
    const known = s.byOngoing[id]
    if (!force && known && known.status !== 'error') return
    commit('SET_ONGOING', { id, status: 'loading', topics: known?.topics || [] })
    try {
      commit('SET_ONGOING', { id, status: 'ready', topics: topicsFrom(unwrap(await api.getV2(`chat/ongoings/${id}/topics`, {}))) })
    } catch {
      commit('SET_ONGOING', { id, status: 'error', topics: known?.topics || [] })
    }
  },

  /** Merge a topic into the one before it, or split one at a question. Both are manual decisions. */
  async merge({ dispatch }, topicId) {
    return dispatch('reshape', { service: `chat/topics/${topicId}/merge-previous` })
  },
  async split({ dispatch }, { topicId, messageId }) {
    return dispatch('reshape', { service: `chat/topics/${topicId}/split`, body: { messageId } })
  },
  async reshape({ commit, dispatch, rootGetters }, { service, body = {} }) {
    if (!usable(rootGetters)) return false
    try {
      await post(service, body)
      commit('CLEAR_TURNS')
      await dispatch('load', { force: true })
      return true
    } catch {
      toast(GC_V2_TEXT.actionFailed, { theme: 'error', autoClose: 4000 })
      return false
    }
  },

  /** The link itself: `POST topics/{id}/link`. Idempotent on the server. Failures are the caller's. */
  async link({ dispatch }, { topicId, ongoingId, packId = null, savedRef = null, source = 'save' }) {
    await post(`chat/topics/${topicId}/link`, { ongoingId, packId, savedRef, source })
    await Promise.all([
      dispatch('load', { force: true }),
      dispatch('ensureOngoing', { ongoingId, force: true }),
    ])
  },

  /** "Remove link" on the Briefing. The topic starts its retention clock again from now. */
  async unlink({ dispatch, rootGetters }, { topicId, ongoingId }) {
    if (!usable(rootGetters)) return false
    try {
      await remove(`chat/topics/${topicId}/link/${ongoingId}`)
      await Promise.all([
        dispatch('load', { force: true }),
        dispatch('ensureOngoing', { ongoingId, force: true }),
      ])
      return true
    } catch {
      toast(GC_V2_TEXT.actionFailed, { theme: 'error', autoClose: 4000 })
      return false
    }
  },

  /** What to suggest for a topic: its sticky destination, else the ranked matches. Names filled in. */
  async matchesFor({ rootGetters }, topicId) {
    const data = unwrap(await post(`chat/topics/${topicId}/suggest-destination`, {}, { timeout: SUGGEST_TIMEOUT_MS }))
    const titleOf = (id) => rootGetters?.['ongoing/byId']?.(id)?.title || ''
    return matchesFrom(data)
      .map((m) => ({ ...m, ongoingTitle: m.ongoingTitle || titleOf(m.ongoingId) }))
      .filter((m) => m.ongoingTitle)
  },

  /** Open the card for `offer`, then ask for a suggestion. `null` matches leave it empty. */
  async beginOffer({ commit, dispatch }, { offer, locate }) {
    offerTicket += 1
    const ticket = offerTicket
    commit('SET_OFFER', { status: 'loading', topicId: null, sessionId: null, matches: [], ...offer })
    try {
      const located = await dispatch('topicFor', locate)
      if (ticket !== offerTicket) return
      if (!located) {
        commit('PATCH_OFFER', { status: 'none' })
        return
      }
      commit('PATCH_OFFER', { topicId: located.topicId, sessionId: located.sessionId })
      const matches = await dispatch('matchesFor', located.topicId)
      if (ticket !== offerTicket) return
      commit('PATCH_OFFER', { status: matches.length ? 'ready' : 'none', matches })
    } catch {
      if (ticket === offerTicket) commit('PATCH_OFFER', { status: 'none' })
    }
  },

  /** "Save conversation", from the rail or the conversation's header (V1's `promptSave` slot). */
  beginConversation({ dispatch, rootGetters }, { id, title } = {}) {
    if (!usable(rootGetters)) return null
    return dispatch('beginOffer', { offer: { mode: 'conversation', targetId: id, title }, locate: { id } })
  },

  /** Research ticked on Home, with no Ongoing yet: the card offers one before the pack card asks. */
  beginSelection({ dispatch, rootGetters }) {
    if (!usable(rootGetters)) return null
    const question = selectionQuestion(rootGetters?.['workboard/items'])
    return dispatch('beginOffer', { offer: { mode: 'selection' }, locate: { id: 'live', question } })
  },

  /** "Choose another" under a suggested task: the card picks, and the block keeps the answer. */
  chooseForBlock({ commit }, { turnId, key: blockKey } = {}) {
    offerTicket += 1
    commit('SET_OFFER', { mode: 'block', status: 'ready', matches: [], choosing: true, turnId, blockKey })
  },

  /** Close the card, dropping any answer still on its way. */
  closeOffer({ commit, dispatch, state: s }) {
    offerTicket += 1
    if (s.offer?.mode === 'selection') commit('SET_SELECTION_DISMISSED', true)
    if (s.offer?.mode === 'conversation') dispatch('chatSessions/cancelSave', null, { root: true })
    commit('SET_OFFER', null)
  },

  /** The selection went away: the next one is asked about afresh. */
  selectionEnded({ commit, state: s }) {
    if (s.selectionDismissed) commit('SET_SELECTION_DISMISSED', false)
    if (s.offer?.mode === 'selection') {
      offerTicket += 1
      commit('SET_OFFER', null)
    }
  },

  /**
   * "Choose another" while research is being saved. The Research Pack card already knows how to
   * choose or name a pack inside an Ongoing, so the card hands over to it with the Ongoing as the
   * target. Saving there is a confirmed save, and `linkFiledPack` links it the same way.
   */
  async handOverSelection({ commit, dispatch }, destination) {
    offerTicket += 1
    commit('SET_SELECTION_DISMISSED', true)
    commit('SET_OFFER', null)
    if (destination?.ongoingId) await dispatch('research/setTarget', destination.ongoingId, { root: true })
  },

  /** Save here. `destination` is `{ kind?, ongoingId, ongoingTitle, packId, packTitle }`. */
  async saveHere({ commit, dispatch, state: s }, destination) {
    const offer = s.offer
    if (!offer || s.offerBusy || !destination) return false
    if (offer.mode === 'block') {
      await dispatch('setBlockDestination', { turnId: offer.turnId, key: offer.blockKey, destination })
      commit('SET_OFFER', null)
      return true
    }
    commit('SET_OFFER_BUSY', true)
    try {
      if (offer.mode === 'selection') return await dispatch('saveSelection', destination)
      return await dispatch('saveConversation', destination)
    } finally {
      commit('SET_OFFER_BUSY', false)
    }
  },

  /**
   * A conversation saved to an Ongoing: a link and nothing else. The turns stay where they were,
   * in the rail and in the session (V1's `assign` is what moves them, and it is not called).
   */
  async saveConversation({ commit, dispatch, state: s }, destination) {
    try {
      let ongoing = { id: destination.ongoingId, title: destination.ongoingTitle }
      if (destination.kind === 'new') {
        const made = await dispatch('ongoing/createFromEntity', { title: destination.ongoingTitle, origin: 'research' }, { root: true })
        if (!made?.id) throw new Error('no ongoing')
        ongoing = { id: made.id, title: made.title || destination.ongoingTitle }
      }
      const located = await dispatch('topicFor', { id: s.offer.targetId })
      if (!located) throw new Error('no topic')
      await dispatch('link', { topicId: located.topicId, ongoingId: ongoing.id, packId: destination.packId || null })
      commit('SET_OFFER', null)
      await dispatch('chatSessions/cancelSave', null, { root: true })
      const to = ongoingRoute(ongoing.id)
      toast(savedConversationText(ongoing.title), {
        theme: 'success',
        autoClose: 6000,
        view: () => { if (to) router.push(to).catch(() => {}) },
        viewLabel: GC_V2_TEXT.open,
      })
      return true
    } catch {
      toast(GC_V2_TEXT.saveFailed, { theme: 'error', autoClose: 4000 })
      return false
    }
  },

  /**
   * The ticked research saved into the suggested destination, the conversation staying on screen so
   * the next action in this topic can use the same destination. It is the box's own save
   * (`ongoing/addResearch`, `research/filedIntoPack`), then place and link. A save that landed and
   * a link that did not are told apart, because the research is safe either way.
   */
  async saveSelection({ commit, dispatch, rootGetters }, destination) {
    const items = rootGetters?.['workboard/items'] || []
    if (!items.length) return false
    const question = selectionQuestion(items)
    commit('SET_OWN_SAVE', true)
    let packId = destination.packId || null
    let packTitle = destination.packTitle || null
    try {
      const story = await dispatch('ongoing/addResearch', { id: destination.ongoingId, items, packId }, { root: true })
      if (!packId) {
        const packs = story?.packs || []
        packId = packs[packs.length - 1]?.id || null
        packTitle = packs[packs.length - 1]?.title || null
      }
      if (packId) await dispatch('research/filedIntoPack', { id: packId, title: packTitle }, { root: true })
    } catch {
      commit('SET_OWN_SAVE', false)
      toast(GC_V2_TEXT.saveFailed, { theme: 'error', autoClose: 4000 })
      return false
    }
    await dispatch('workboard/clear', null, { root: true })
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('ongoing-advanced', { detail: destination.ongoingId }))
    commit('SET_OFFER', null)
    let linked = false
    try {
      const located = await dispatch('topicFor', { id: 'live', question })
      if (located) {
        await dispatch('link', { topicId: located.topicId, ongoingId: destination.ongoingId, packId, savedRef: savedRefsFrom({ packId })[0] || null })
        linked = true
      }
    } catch {
      linked = false
    }
    commit('SET_OWN_SAVE', false)
    const to = ongoingRoute(destination.ongoingId)
    toast(linked ? savedSelectionText(items.length, destination.ongoingTitle) : GC_V2_TEXT.linkFailed, {
      theme: linked ? 'success' : 'info',
      autoClose: 6000,
      view: () => { if (to) router.push(to).catch(() => {}) },
      viewLabel: GC_V2_TEXT.open,
    })
    return true
  },

  /**
   * The save hook for research saved through the Research Pack card from Home. A save made inside
   * an Ongoing is that Ongoing's own and has no Global Conversation topic to link.
   */
  async linkFiledPack({ state: s, dispatch, rootGetters }, { packId, question = '' }) {
    if (s.ownSave || !packId || !usable(rootGetters)) return false
    const scope = String(rootGetters?.['research/sessionScope'] || 'home')
    const ongoingId = rootGetters?.['research/targetOngoingId']
    if (scope !== 'home' || !ongoingId || !isHomePath(router.currentRoute?.value?.path)) return false
    try {
      // The question the ticked research was asked under (read by the plugin before the board was
      // cleared) is the one that finds its topic: the research session's latest question is the
      // wrong one once the user has searched again.
      const located = await dispatch('topicFor', { id: 'live', question: question || rootGetters?.['research/query'] || '' })
      if (!located) return false
      await dispatch('link', { topicId: located.topicId, ongoingId, packId, savedRef: savedRefsFrom({ packId })[0] || null })
      return true
    } catch {
      return false
    }
  },

  /** The save hook for a Task made from the chat, when the user gave it a destination. */
  async linkCreatedTask({ dispatch, rootGetters }, { task, turn, destination }) {
    if (!task?.id || !destination?.ongoingId || !usable(rootGetters)) return false
    try {
      const located = await dispatch('topicFor', { id: 'live', messageId: answerMessageId(turn), question: turn?.question || '' })
      if (!located) return false
      await dispatch('link', {
        topicId: located.topicId,
        ongoingId: destination.ongoingId,
        packId: destination.packId || null,
        savedRef: savedRefsFrom({ taskId: task.id })[0] || null,
      })
      return true
    } catch {
      return false
    }
  },

  /**
   * A suggested Task gets its destination SHOWN before it is created (PDF p2, "Then you create a
   * Task"). This only fills the block in memory, and pressing Create is what confirms it; a topic
   * with nothing to suggest leaves the block as it was.
   */
  async suggestForBlock({ dispatch, rootGetters }, { turn, block } = {}) {
    if (!usable(rootGetters) || !turn?.id || !block?.key || block.ongoingId || block.gcV2Asked || block.state || turn.restored) return
    await dispatch('timelineSearch/patchBlock', { turnId: turn.id, key: block.key, patch: { gcV2Asked: true } }, { root: true })
    try {
      const located = await dispatch('topicFor', { id: 'live', messageId: answerMessageId(turn), question: turn.question || '' })
      if (!located) return
      const [match] = await dispatch('matchesFor', located.topicId)
      if (match) await dispatch('setBlockDestination', { turnId: turn.id, key: block.key, destination: match })
    } catch {
      // No suggestion: Create behaves exactly as it does without V2.
    }
  },

  setBlockDestination({ dispatch }, { turnId, key: blockKey, destination }) {
    const patch = { ongoingId: destination?.ongoingId || null, gcV2Destination: destination || null }
    return dispatch('timelineSearch/patchBlock', { turnId, key: blockKey, patch }, { root: true })
  },
}

export const mutations = {
  SET_STATUS(s, status) { s.status = status },
  MERGE_PAGE(s, page) { s.topics = mergeNewestPage(s.topics, page) },
  PREPEND(s, page) {
    const known = new Set(s.topics.map((t) => t.id))
    s.topics = [...page.filter((t) => !known.has(t.id)), ...s.topics]
  },
  ADD_TOPIC(s, topic) {
    s.topics = [...s.topics.filter((t) => t.id !== topic.id), topic]
      .sort((a, b) => String(a.startedUtc).localeCompare(String(b.startedUtc)))
  },
  SET_HAS_MORE(s, v) { s.hasMore = !!v },
  SET_LOADING_MORE(s, v) { s.loadingMore = !!v },
  SET_ONGOING(s, { id, status, topics }) { s.byOngoing = { ...s.byOngoing, [id]: { status, topics } } },
  SET_TURNS(s, { id, turns }) { s.turnsById = { ...s.turnsById, [id]: turns } },
  CLEAR_TURNS(s) { s.turnsById = {} },
  SET_OFFER(s, offer) { s.offer = offer || null },
  PATCH_OFFER(s, patch) { if (s.offer) s.offer = { ...s.offer, ...patch } },
  SET_OFFER_BUSY(s, v) { s.offerBusy = !!v },
  SET_SELECTION_DISMISSED(s, v) { s.selectionDismissed = !!v },
  SET_OWN_SAVE(s, v) { s.ownSave = !!v },
}
